import React from 'react';
import { Formik, useFormikContext } from 'formik';
import { Box, Chip, Typography } from '@mui/material';
import { updatePatientAllergies } from '@uplift/api';
import { usePatientAllergiesQuery } from '@uplift/queries';
import { PsychiatryNoteSection } from '../components/PsychiatryNoteSection.component';
import { YesNoRadioButtonGroup } from '../../../Inputs';
import { FormikAutoSave } from '../../../ServerForm';
import { FloatingActionBar } from '../components/FloatingActionBar.component';
import { Button } from '../../../Button';
import { AllergiesForm } from '../../ProviderPatientProfile/AllergiesForm/AllergiesForm.component';
import { useL10n } from '@uplift/hooks';


export const AllergiesSectionForm: React.VFC<{ autoSave?: boolean }> = ({ autoSave = true }) => {
  const formik = useFormikContext<{ isAllergies: boolean }>();

  return (
    <>
      <Box px={3} pb={2}>
        <YesNoRadioButtonGroup size="small" label="Does the client have any allergies?" name="isAllergies" asBoolean />
      </Box>
      {formik.values.isAllergies ? (
        <Box px={3} py={2} bgcolor="grey.100">
          <AllergiesForm />
        </Box>
      ) : null}
      {autoSave ? <FormikAutoSave /> : null}
    </>
  );
};


export const AllergiesSectionFormik: React.FC<{ patientId: string; onSuccess?: () => void }> = ({ patientId, onSuccess, children }) => {
  const { data: allergies, refetch } = usePatientAllergiesQuery(patientId, { suspense: true });
  const hasAllergies = (allergies?.length ?? 0) > 0;

  return (
    <Formik
      initialValues={hasAllergies ? {
        isAllergies: hasAllergies,
        allergies: allergies?.map(allergy => allergy.name) ?? [],
      } : {
        isAllergies: null,
        allergies: [],
      }}
      onSubmit={async (values) => {
        const allergies = values.isAllergies ? values.allergies : [];

        await updatePatientAllergies(patientId, allergies);
        await refetch();
        onSuccess?.();
      }}
    >
      {children}
    </Formik>
  );
};


export const AllergiesEditSection: React.VFC<{ patientId: string }> = ({ patientId }) => {
  return (
    <PsychiatryNoteSection title="Allergies">
      <AllergiesSectionFormik patientId={patientId}>
        <AllergiesSectionForm />
      </AllergiesSectionFormik>
    </PsychiatryNoteSection>
  );
};


export const AllergiesViewSection: React.VFC<{ patientId: string; onEdit?: () => void }> = ({ patientId, onEdit }) => {
  const l10n = useL10n();
  const { data: allergiesData } = usePatientAllergiesQuery(patientId, { suspense: true });

  return (
    <PsychiatryNoteSection
      title="Allergies"
      actions={onEdit && (
        <Button
          onClick={onEdit}
          size="small"
          color="secondary"
        >
          Edit
        </Button>
      )}
    >
      <Box px={3} display="flex" gap="16px">
        {allergiesData?.length ? allergiesData.map(h => (
          <Chip
            key={h.id}
            label={h.name}
            variant="outlined"
            style={{ borderRadius: 'unset' }}
          />
        )) : (
          <Typography>{l10n.clientProfile.allergies.noAllergies}</Typography>
        )}
      </Box>
    </PsychiatryNoteSection>
  );
};


export const AllergiesSection: React.VFC<{ patientId: string }> = ({ patientId }) => {
  const [ isEditing, setIsEditing ] = React.useState(false);

  if (isEditing) {
    return (
      <AllergiesSectionFormik patientId={patientId} onSuccess={() => setIsEditing(false)}>
        <>
          <PsychiatryNoteSection title="Allergies">
            <AllergiesSectionForm autoSave={false} />
          </PsychiatryNoteSection>
          <FloatingActionBar onCancel={() => setIsEditing(false)} />
        </>
      </AllergiesSectionFormik>
    );
  }

  return (
    <AllergiesViewSection patientId={patientId} onEdit={() => setIsEditing(true)} />
  );
};
